<template>
  <div>
    <!-- TODO: change this component to use EOS iframe and an EOS page within -->
    <div v-if="selectedSource" class="layer-info">
      <div
        v-if="selectedSource.description"
        class="mb-3 layer-info-description"
      >
        {{ selectedSource.description }}
      </div>
      <div
        v-for="(layer, index) in selectedLayers"
        :key="layer.id"
        :class="{ 'mb-3': index !== selectedLayers.length - 1 }"
      >
        <div class="application-heading">{{ layer.title }}</div>
        <img v-if="layer.legendUrl" :src="layer.legendUrl" />
        <div v-if="layer.legendByStyle">
          <v-row
            v-for="(item, index) in getLegendByStyleArray(layer)"
            :key="index"
            align="center"
            class="ml-5"
          >
            <v-col cols="auto" class="pa-0 ma-0">
              <div
                :class="{
                  'legend-box': item.type === 'box',
                  'legend-circle': item.type === 'circle',
                }"
                :style="`background-color: ${item.color}`"
              ></div>
            </v-col>
            <v-col class="pa-0 ma-0 ml-3">
              <span>{{ item.text }}</span>
            </v-col>
          </v-row>
        </div>
      </div>
      <div
        v-if="attribution"
        class="my-3 layer-info-description"
        v-html="attribution"
      ></div>
    </div>
  </div>
</template>

<script>
import lodashFind from "lodash/find";

import {
  MODULE_PREFIX_MAP_CONTROLS,
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_MAP,
} from "@/store";
import {
  GET_MAP_CONTROLS_EXTERNAL_REFRESH_MAP,
  GET_DETAILS_SIDEBAR,
} from "@/store/actions.type";
import { removeMapData, addWMSData } from "@/common/maps/features";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import { waitForMapReady } from "@/common/maps/maps";
import { getUrlDomain } from "@/common/basic.utilities";
import {
  MAP_LAYER_METADATA_EXTERNAL,
  EXTERNAL_SOURCES_WMS,
  EXTERNAL_SOURCES_TILES,
} from "@/common/constants/mapControls";
import { MAP_TILE_SERVER_DYNAMIC } from "@/common/config";
import {
  addTilesToMap,
  getExternalLayersConfig,
} from "@/services/mapControls/mapControls.service";

import { mapGetters } from "vuex";

export default {
  name: "MapControlsExternal",
  components: {},
  data: () => ({
    layerId: MAP_LAYER_METADATA_EXTERNAL,
    externalConfig: undefined,
    hasExternalMapChanged: false,
  }),
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      mainMapHijacker: MODULE_PREFIX_MAP + "mainMapHijacker",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
      mapControlParams: MODULE_PREFIX_MAP_CONTROLS + "params",
      refreshMap: MODULE_PREFIX_MAP_CONTROLS + "externalRefreshMap",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    selectedSource() {
      return this.mapControlParams?.mapExternalControlSelectedId
        ? lodashFind(this.externalConfig, {
            id: this.mapControlParams.mapExternalControlSelectedId,
          })
        : null;
    },
    selectedLayers() {
      if (this.selectedSource && this.selectedSource?.layers) {
        if (this.selectedSource?.type === EXTERNAL_SOURCES_WMS) {
          return this.selectedSource.layers.flatMap(
            (layer) =>
              layer.sublayers?.filter((sublayer) => sublayer.enabled) || []
          );
        } else if (this.selectedSource?.type === EXTERNAL_SOURCES_TILES) {
          return this.selectedSource.layers;
        }
      }
      return null;
    },
    attribution() {
      if (this.selectedSource?.attribution) {
        const urlRegex = /https?:\/\/[^\s]+/g;
        return this.selectedSource.attribution.replace(
          urlRegex,
          (url) => `<a href="${url}" target="_blank">${getUrlDomain(url)}</a>`
        );
      }
      return null;
    },
  },
  async mounted() {
    this.externalConfig = await getExternalLayersConfig();
  },
  methods: {
    getTileBaseUrl(layer) {
      let url = layer.urlSeerist
        ? `${MAP_TILE_SERVER_DYNAMIC}${layer.urlSeerist}`
        : layer.url;

      // look for ||time|| string in url and replace with current datetime (needed for meteoblue)
      url = url.replace(
        "||time||",
        new Date().toISOString().substring(0, 13) + ":00Z"
      );

      return url;
    },
    buildTileUrl(url, filters) {
      const params = new URLSearchParams(filters).toString();
      return params && params.length ? url + "?" + params : url;
    },
    addToMap() {
      if (this.selectedSource) {
        if (this.selectedSource?.type === EXTERNAL_SOURCES_TILES) {
          if (!this.selectedSource.layers) {
            console.error(
              "External map layer config not valid, missing information: " +
                this.selectedSource?.type +
                " - " +
                this.selectedSource?.id
            );
            return;
          }

          const tileUrl = this.getTileBaseUrl(this.selectedSource);
          if (!tileUrl) {
            console.error("External map layer tileUrl not valid " + tileUrl);
            return;
          }

          this.selectedSource.layers.forEach((layer) => {
            addTilesToMap({
              map: this.map,
              layerId: `layer-${this.layerId}-${layer.id}`,
              layerSource: `${this.layerId}-${layer.id}`,
              layerSourceType: layer.sourceLayerType || "vector",
              layerType: layer.layerType || "fill",
              layerMetadata: this.layerId,
              layerSourceLayer: layer.sourceLayer,
              tileUrl: this.buildTileUrl(tileUrl, layer.filters),
              layerOptions: layer.layerOptions,
              mapClickEvent: this.handleMapClickEvent,
            });
          });
        } else if (
          this.selectedSource?.type === EXTERNAL_SOURCES_WMS &&
          this.selectedSource?.url &&
          this.selectedLayers
        ) {
          this.selectedLayers.forEach((layer) => {
            addWMSData({
              layerId: `${this.layerId}-${layer.id}`,
              wmsUrl: this.selectedSource.url,
              layerName: layer.name,
              styleName: layer.style,
              map: this.map,
              ...(this.selectedSource.paint && {
                paint: this.selectedSource.paint,
              }),
              ...(this.selectedSource.format && {
                format: this.selectedSource.format,
              }),
              ...(this.selectedSource.transparent && {
                transparent: this.selectedSource.transparent,
              }),
              ...(this.selectedSource.version && {
                version: this.selectedSource.version,
              }),
              ...(this.selectedSource.height && {
                height: this.selectedSource.height,
              }),
              ...(this.selectedSource.width && {
                width: this.selectedSource.width,
              }),
              ...(this.selectedSource.srs && { srs: this.selectedSource.srs }),
              ...(this.selectedSource.bbox && {
                bbox: this.selectedSource.bbox,
              }),
            });
          });
        }

        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.map,
          EVENT_ACTIONS.addDataToMap,
          this.selectedSource.id
        );
      }
    },
    handleMapClickEvent(e, features) {
      e.originalEvent.stopPropagation();

      if (features.length > 0) {
        setTimeout(async () => {
          const layerObj = {};
          if (features[0].layer?.["source-layer"]) {
            const layer = this.selectedLayers.find(
              (layer) => layer.sourceLayer === features[0].layer["source-layer"]
            );
            if (layer?.clickProperties) {
              Object.keys(layer.clickProperties).forEach((key) => {
                let value = layer.clickProperties[key];
                if (isNaN(key)) {
                  const matches = value.match(/\[(.*?)\]/g);
                  if (matches) {
                    matches.forEach((match) => {
                      const matchTxt = match?.replace("[", "").replace("]", "");
                      value = value.replace(
                        match,
                        features[0].properties?.[matchTxt]
                      );
                    });
                  }
                  layerObj[key] = value;
                } else {
                  if (features[0].properties?.[value]) {
                    layerObj[value] = features[0].properties?.[value];
                  }
                }
              });
            }
          }

          if (Object.keys(layerObj).length > 0) {
            this.detailsSidebar.visible = true;
            this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.mapControls;
            this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.mapControls;
            this.detailsSidebar.title = this.selectedSource?.title;
            this.detailsSidebar.features = [];
            this.detailsSidebar.features.push(layerObj);

            await this.$store.dispatch(
              MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
              this.detailsSidebar
            );
          }
        }, 300);
      }
    },
    removeFromMap() {
      if (
        this.map &&
        this.selectedSource?.type === "WMS" &&
        this.selectedLayers
      ) {
        this.selectedLayers.forEach((layer) => {
          removeMapData(this.map, `${this.layerId}-${layer.id}`);
        });
      }
    },
    clearAllExternalLayersFromMap() {
      const layers = this.map?.getStyle()?.layers;
      if (layers) {
        layers.some((layer) => {
          if (layer.id.includes(this.layerId)) {
            removeMapData(this.map, layer.id);
          }
        });
      }
    },
    handleMapRefresh(force) {
      if (this.refreshMap || force) {
        if (
          this.mapControlParams?.mapExternalControlEnabled &&
          this.mapControlParams?.mapExternalControlSelectedId &&
          !this.mainMapHijacker
        ) {
          if (this.hasExternalMapChanged) {
            this.hasExternalMapChanged = false;
            this.attemptAddToMap();
          }
        } else {
          this.removeFromMap();
        }
        this.$store.dispatch(
          MODULE_PREFIX_MAP_CONTROLS + GET_MAP_CONTROLS_EXTERNAL_REFRESH_MAP,
          false
        );
      }
    },
    async attemptAddToMap(attemptCount = 0) {
      if (!this.map && attemptCount < 50) {
        setTimeout(() => {
          this.attemptAddToMap(attemptCount + 1);
        }, 500);
      } else {
        await waitForMapReady(this.map);
        this.addToMap();
      }
    },
    getLegendByStyleArray(layer) {
      if (
        layer?.layerType === "fill" &&
        layer?.layerOptions?.paint?.["fill-color"] &&
        Array.isArray(layer.layerOptions.paint["fill-color"])
      ) {
        // Ignore the first three elements and extract the rest
        const relevantArray = layer.layerOptions.paint["fill-color"].slice(3);

        const result = [];
        for (let i = 0; i < relevantArray.length - 2; i += 2) {
          const currentValue = relevantArray[i];
          const nextValue = relevantArray[i + 2];
          const color = relevantArray[i + 1];
          result.push({
            type: "box",
            text: `${currentValue}–${nextValue} mph`,
            color: color,
          });
        }
        // Add the last element explicitly
        const lastValue = relevantArray[relevantArray.length - 2];
        const lastColor = relevantArray[relevantArray.length - 1];
        result.push({
          type: "box",
          text: `${lastValue}+ mph`,
          color: lastColor,
        });
        return result;
      } else if (
        layer?.layerType === "circle" &&
        layer?.layerOptions?.paint?.["circle-color"]
      ) {
        const result = [];
        result.push({
          type: "circle",
          text: layer?.title ? layer.title : this.selectedSource?.title,
          color: layer.layerOptions.paint["circle-color"],
        });
        return result;
      }
      return null;
    },
  },
  watch: {
    mainMapHijacker: {
      handler(newVal, oldVal) {
        this.handleMapRefresh(newVal !== oldVal);
      },
    },
    refreshMap: {
      handler(val) {
        if (val !== true && val !== false) {
          this.hasExternalMapChanged = true;
          this.clearAllExternalLayersFromMap();
        }
        this.handleMapRefresh(true);
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style scoped>
.layer-info {
  font-size: 0.85rem;
}
.layer-info-description {
  font-size: 0.7rem;
  line-height: 0.9rem;
}
.legend-box {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}
.legend-circle {
  width: 16px;
  height: 16px;
  border-radius: 8px;
}
</style>
